import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";

import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import { MentionsLegales, CGU, PolitiqueConfidentialite, PolitiqueCookies } from "./pages/LegalPages";
import Tarifs from "./pages/Tarifs";
import QuittanceDeLoyer from "./pages/seo/QuittanceDeLoyer";
import GenerateurQuittance from "./pages/GenerateurQuittance";
import SimulateurLmnp2027 from "./pages/SimulateurLmnp2027";
import CalculateurIrl from "./pages/CalculateurIrl";
import NewsletterDesinscription from "./pages/NewsletterDesinscription";
import RevisionLoyerIrl from "./pages/seo/RevisionLoyerIrl";
import DeclarationRevenusFonciers from "./pages/seo/DeclarationRevenusFonciers";
import GestionADistance from "./pages/GestionADistance";
import DevenirReferent from "./pages/DevenirReferent";
import BlogIndex from "./pages/blog/BlogIndex";
import BlogArticle from "./pages/blog/BlogArticle";
import SignLease from "./pages/SignLease";
import SignEdl from "./pages/SignEdl";
import PublicReferentReview from "./pages/PublicReferentReview";
import PublicMissionBooking from "./pages/PublicMissionBooking";
import PublicEdlView from "./pages/PublicEdlView";
import VerifySeal from "./pages/VerifySeal";
import RoleSelection from "./pages/RoleSelection";
import VerifyEmail from "./pages/VerifyEmail";
import ResetPassword from "./pages/ResetPassword";
import Demo from "./pages/Demo";
import PaymentResult from "./pages/PaymentResult";
import AuthCallback from "./components/auth/AuthCallback";
import DashboardLayout from "./components/dashboard/DashboardLayout";
import DashboardHome from "./pages/dashboard/DashboardHome";
import Biens from "./pages/dashboard/Biens";
import BienDetail from "./pages/dashboard/BienDetail";
import Locataires from "./pages/dashboard/Locataires";
import LocataireDetail from "./pages/dashboard/LocataireDetail";
import Locations from "./pages/dashboard/Locations";
import Quittances from "./pages/dashboard/Quittances";
import ModelesDocuments from "./pages/dashboard/ModelesDocuments";
import JournalEnvois from "./pages/dashboard/JournalEnvois";
import Coffre from "./pages/dashboard/Coffre";
import MonEspace from "./pages/dashboard/MonEspace";
import Finances from "./pages/dashboard/Finances";
import Prets from "./pages/dashboard/Prets";
import Rapprochement from "./pages/dashboard/Rapprochement";import BilanPrevisionnel from "./pages/dashboard/BilanPrevisionnel";import Profil from "./pages/dashboard/Profil";import Messagerie from "./pages/dashboard/Messagerie";
import AssistantIA from "./pages/dashboard/AssistantIA";
import Assurances from "./pages/dashboard/Assurances";
import MesAssurances from "./pages/dashboard/MesAssurances";
import EspaceAssureur from "./pages/dashboard/EspaceAssureur";
import Artisans from "./pages/dashboard/Artisans";
import Travaux from "./pages/dashboard/Travaux";
import ActiviteArtisan from "./pages/dashboard/ActiviteArtisan";
import Admin from "./pages/dashboard/Admin";
import Actualites from "./pages/dashboard/Actualites";
import OffresDeals from "./pages/dashboard/OffresDeals";
import EtatsDesLieux from "./pages/dashboard/EtatsDesLieux";
import Referent from "./pages/dashboard/Referent";
import Referents from "./pages/dashboard/Referents";
import ReferentMessagerie from "./pages/dashboard/ReferentMessagerie";
import ReferentCalendrier from "./pages/dashboard/ReferentCalendrier";
import ReferentDashboard from "./pages/dashboard/ReferentDashboard";
import EdlHistorique from "./pages/dashboard/EdlHistorique";
import MesDocumentsEdl from "./pages/dashboard/MesDocumentsEdl";
import Simulateur from "./pages/dashboard/Simulateur";
import SaisonnierDashboard from "./pages/dashboard/saisonnier/SaisonnierDashboard";
import SaisonnierCalendrier from "./pages/dashboard/saisonnier/SaisonnierCalendrier";
import SaisonnierReservations from "./pages/dashboard/saisonnier/SaisonnierReservations";
import SaisonnierTarifs from "./pages/dashboard/saisonnier/SaisonnierTarifs";
import SaisonnierConformite from "./pages/dashboard/saisonnier/SaisonnierConformite";
import SaisonnierBien from "./pages/dashboard/saisonnier/SaisonnierBien";
import SaisonnierMissions from "./pages/dashboard/saisonnier/SaisonnierMissions";
import SaisonnierTaxe from "./pages/dashboard/saisonnier/SaisonnierTaxe";
import SaisonnierVersements from "./pages/dashboard/saisonnier/SaisonnierVersements";
import SaisonnierLivret from "./pages/dashboard/saisonnier/SaisonnierLivret";
import SaisonnierFiscalite from "./pages/dashboard/saisonnier/SaisonnierFiscalite";
import LivretAccueil from "./pages/LivretAccueil";
import SaisonnierDirect from "./pages/dashboard/saisonnier/SaisonnierDirect";
import ReserverDirect from "./pages/ReserverDirect";
import SejourVoyageur from "./pages/SejourVoyageur";
import AvisVoyageur from "./pages/AvisVoyageur";
import SaisonnierRapports from "./pages/dashboard/saisonnier/SaisonnierRapports";
import SaisonnierSerrures from "./pages/dashboard/saisonnier/SaisonnierSerrures";
import MissionIntervenant from "./pages/MissionIntervenant";
import { ComingSoon } from "./components/dashboard/ComingSoon";
import { GraduationCap, Compass, Calculator, Landmark, BookOpen } from "lucide-react";

function ProtectedRoute({ children, requireRole = true }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/connexion" replace />;
  if (requireRole && !user.role) return <Navigate to="/choisir-role" replace />;
  return children;
}

function AppIndex() {
  const { user } = useAuth();
  if (localStorage.getItem("pending_upgrade")) return <Navigate to="/app/profil" replace />;
  if (user?.role === "assureur") return <Navigate to="/app/espace-assureur" replace />;
  if (user?.role === "artisan") return <Navigate to="/app/activite" replace />;
  if (user?.role === "locataire") return <Navigate to="/app/mon-espace" replace />;
  if (user?.role === "admin") return <Navigate to="/app/admin" replace />;
  if (user?.role === "referent") return <Navigate to="/app/referent-dashboard" replace />;
  return <DashboardHome />;
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/mentions-legales" element={<MentionsLegales />} />
      <Route path="/cgu" element={<CGU />} />
      <Route path="/politique-de-confidentialite" element={<PolitiqueConfidentialite />} />
      <Route path="/politique-de-cookies" element={<PolitiqueCookies />} />
      <Route path="/tarifs" element={<Tarifs />} />
      <Route path="/quittance-de-loyer" element={<QuittanceDeLoyer />} />
      <Route path="/quittance-de-loyer/generateur" element={<GenerateurQuittance />} />
      <Route path="/simulateur-lmnp-2027" element={<SimulateurLmnp2027 />} />
      <Route path="/mission/:token" element={<MissionIntervenant />} />
      <Route path="/livret/:token" element={<LivretAccueil />} />
      <Route path="/reserver/:token" element={<ReserverDirect />} />
      <Route path="/sejour/:token" element={<SejourVoyageur />} />
      <Route path="/avis/:token" element={<AvisVoyageur />} />
      <Route path="/newsletter/desinscription" element={<NewsletterDesinscription />} />
      <Route path="/revision-loyer-irl" element={<RevisionLoyerIrl />} />
      <Route path="/revision-loyer-irl/calculateur" element={<CalculateurIrl />} />
      <Route path="/declaration-revenus-fonciers" element={<DeclarationRevenusFonciers />} />
      <Route path="/gestion-locative-a-distance" element={<GestionADistance />} />
      <Route path="/devenir-referent" element={<DevenirReferent />} />
      <Route path="/blog" element={<BlogIndex />} />
      <Route path="/blog/:slug" element={<BlogArticle />} />
      <Route path="/connexion" element={<LoginPage />} />
      <Route path="/verifier-email" element={<VerifyEmail />} />
      <Route path="/reinitialiser-mot-de-passe" element={<ResetPassword />} />
      <Route path="/demo" element={<Demo />} />
      <Route path="/app/demo" element={<Navigate to="/demo" replace />} />
      <Route path="/choisir-role" element={<ProtectedRoute requireRole={false}><RoleSelection /></ProtectedRoute>} />
      <Route path="/payment/success" element={<PaymentResult mode="success" />} />
      <Route path="/payment/cancel" element={<PaymentResult mode="cancel" />} />
      <Route path="/sign/:token" element={<SignLease />} />
      <Route path="/sign-edl/:token" element={<SignEdl />} />
      <Route path="/evaluer-referent/:token" element={<PublicReferentReview />} />
      <Route path="/reserver-creneau/:token" element={<PublicMissionBooking />} />
      <Route path="/etat-des-lieux/:token" element={<PublicEdlView />} />
      <Route path="/verifier/:token" element={<VerifySeal />} />
      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<AppIndex />} />
        <Route path="biens" element={<Biens />} />
        <Route path="biens/:id" element={<BienDetail />} />
        <Route path="locataires" element={<Locataires />} />
        <Route path="locataires/:id" element={<LocataireDetail />} />
        <Route path="locations" element={<Locations />} />
        <Route path="quittances" element={<Quittances />} />
        <Route path="modeles-documents" element={<ModelesDocuments />} />
        <Route path="journal-envois" element={<JournalEnvois />} />
        <Route path="coffre" element={<Coffre />} />
        <Route path="etats-des-lieux" element={<EtatsDesLieux />} />
        <Route path="referent" element={<Referent />} />
        <Route path="referents" element={<Referents view="directory" />} />
        <Route path="referent-missions" element={<Referents view="missions" />} />
        <Route path="referent-releves" element={<Referents view="statements" />} />
        <Route path="referent-profil" element={<Referents view="profile" />} />
        <Route path="referent-messagerie" element={<ReferentMessagerie />} />
        <Route path="referent-calendrier" element={<ReferentCalendrier />} />
        <Route path="referent-dashboard" element={<ReferentDashboard />} />
        <Route path="edl-historique" element={<EdlHistorique />} />
        <Route path="mes-documents" element={<MesDocumentsEdl />} />
        <Route path="mon-espace" element={<MonEspace />} />
        <Route path="finances" element={<Finances />} />
        <Route path="saisonnier" element={<SaisonnierDashboard />} />
        <Route path="saisonnier/calendrier" element={<SaisonnierCalendrier />} />
        <Route path="saisonnier/reservations" element={<SaisonnierReservations />} />
        <Route path="saisonnier/tarifs" element={<SaisonnierTarifs />} />
        <Route path="saisonnier/conformite" element={<SaisonnierConformite />} />
        <Route path="saisonnier/biens/:pid" element={<SaisonnierBien />} />
        <Route path="saisonnier/missions" element={<SaisonnierMissions />} />
        <Route path="saisonnier/taxe-sejour" element={<SaisonnierTaxe />} />
        <Route path="saisonnier/versements" element={<SaisonnierVersements />} />
        <Route path="saisonnier/livret" element={<SaisonnierLivret />} />
        <Route path="saisonnier/fiscalite" element={<SaisonnierFiscalite />} />
        <Route path="saisonnier/reservation-directe" element={<SaisonnierDirect />} />
        <Route path="saisonnier/serrures" element={<SaisonnierSerrures />} />
        <Route path="saisonnier/rapports" element={<SaisonnierRapports />} />
        <Route path="finances/prets" element={<Prets />} />
        <Route path="finances/rapprochement" element={<Rapprochement />} />
        <Route path="finances/bilan" element={<BilanPrevisionnel />} />
        <Route path="profil" element={<Profil />} />
        <Route path="messagerie" element={<Messagerie />} />
        <Route path="assistant-ia" element={<AssistantIA />} />
        <Route path="assurances" element={<Assurances />} />
        <Route path="mes-assurances" element={<MesAssurances />} />
        <Route path="espace-assureur" element={<EspaceAssureur />} />
        <Route path="artisans" element={<Artisans />} />
        <Route path="travaux" element={<Travaux />} />
        <Route path="activite" element={<ActiviteArtisan />} />
        <Route path="admin" element={<Admin />} />
        <Route path="actualites" element={<Actualites />} />
        <Route path="offres-deals" element={<OffresDeals />} />
        <Route path="formation" element={<ComingSoon title="Formation" Icon={GraduationCap} description="Bientôt : formations, vidéos, podcasts et lectures pour développer vos connaissances immobilières." />} />
        <Route path="coach" element={<ComingSoon title="Mon Coach" Icon={Compass} description="Votre accompagnement personnalisé arrive très prochainement." />} />
        <Route path="simulateur" element={<Simulateur />} />
        <Route path="financement" element={<ComingSoon title="Offre de financement" Icon={Landmark} description="Comparez et accédez aux meilleures offres de financement. Bientôt disponible." />} />
        <Route path="articles" element={<ComingSoon title="Articles" Icon={BookOpen} description="Conseils et articles sur l'entretien et les réparations. Bientôt disponible." />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
